import React, { useCallback, useEffect, useRef, useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || "";

const BTN_STYLE = {
  background: "#111827",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "#E5E7EB",
};

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001 6.19 5.238 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
  );
}

function GithubIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true">
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222 0 1.606-.014 2.898-.014 3.293 0 .322.216.694.825.576C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}

function OAuthButton({ icon, label, onClick, loading, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      style={BTN_STYLE}
      className="w-full h-12 rounded-2xl flex items-center justify-center gap-3 text-sm font-medium transition-all duration-200 hover:border-white/25 hover:shadow-[0_0_18px_rgba(255,255,255,0.08)] disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <>
          {icon}
          <span>{label}</span>
        </>
      )}
    </button>
  );
}

/**
 * Isolated Google button. `useGoogleLogin` initializes the Google code client
 * on mount, which throws if no client_id is configured — so this component is
 * only rendered when REACT_APP_GOOGLE_CLIENT_ID is present.
 */
function GoogleAuthButton({ pending, setPending, onSuccess, reportError, disabled }) {
  const { loginWithOAuth } = useAuth();

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      try {
        // 'postmessage' is the implicit redirect_uri for the popup auth-code flow.
        await loginWithOAuth("google", code, "postmessage");
        setPending(null);
        if (onSuccess) onSuccess();
      } catch (err) {
        reportError(err.response?.data?.detail || "Google sign-in failed. Please try again.");
      }
    },
    onError: () => reportError("Google sign-in was cancelled or failed."),
    onNonOAuthError: () => setPending(null), // popup closed by user — no error toast
  });

  return (
    <OAuthButton
      icon={<GoogleIcon />}
      label="Continue with Google"
      onClick={() => {
        setPending("google");
        googleLogin();
      }}
      loading={pending === "google"}
      disabled={disabled}
    />
  );
}

export default function OAuthButtons({ onSuccess, onError }) {
  const { loginWithOAuth } = useAuth();
  const [pending, setPending] = useState(null); // 'google' | 'github' | null
  const githubStateRef = useRef(null);
  const githubPopupRef = useRef(null);

  const reportError = useCallback(
    (message) => {
      setPending(null);
      if (onError) onError(message);
    },
    [onError],
  );

  // ----- GitHub (auth-code web flow via popup) -----
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.source !== "scopuz-github-oauth") return;

      if (data.error) {
        reportError("GitHub sign-in was cancelled or failed.");
        return;
      }
      if (!data.code || data.state !== githubStateRef.current) {
        reportError("GitHub sign-in failed. Please try again.");
        return;
      }

      try {
        const redirectUri = `${window.location.origin}/oauth/github/callback`;
        await loginWithOAuth("github", data.code, redirectUri);
        setPending(null);
        if (onSuccess) onSuccess();
      } catch (err) {
        reportError(err.response?.data?.detail || "GitHub sign-in failed. Please try again.");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [loginWithOAuth, onSuccess, reportError]);

  // Detect a popup the user closed before completing the flow.
  useEffect(() => {
    if (pending !== "github") return undefined;
    const timer = setInterval(() => {
      if (githubPopupRef.current && githubPopupRef.current.closed) {
        clearInterval(timer);
        setPending((p) => (p === "github" ? null : p));
      }
    }, 600);
    return () => clearInterval(timer);
  }, [pending]);

  const handleGithub = () => {
    if (!GITHUB_CLIENT_ID) {
      reportError("GitHub sign-in is not configured.");
      return;
    }
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
    githubStateRef.current = state;
    setPending("github");

    const redirectUri = `${window.location.origin}/oauth/github/callback`;
    const url =
      `https://github.com/login/oauth/authorize?client_id=${encodeURIComponent(GITHUB_CLIENT_ID)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent("read:user user:email")}` +
      `&state=${encodeURIComponent(state)}`;

    const w = 520;
    const h = 640;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    githubPopupRef.current = window.open(
      url,
      "scopuz-github-oauth",
      `width=${w},height=${h},left=${left},top=${top}`,
    );

    if (!githubPopupRef.current) {
      reportError("Popup blocked. Please allow popups and try again.");
    }
  };

  return (
    <div className="space-y-3" data-testid="oauth-buttons">
      {GOOGLE_CLIENT_ID ? (
        <GoogleAuthButton
          pending={pending}
          setPending={setPending}
          onSuccess={onSuccess}
          reportError={reportError}
          disabled={pending !== null}
        />
      ) : (
        <OAuthButton
          icon={<GoogleIcon />}
          label="Continue with Google"
          onClick={() => reportError("Google sign-in is not configured.")}
          disabled={pending !== null}
        />
      )}
      <OAuthButton
        icon={<GithubIcon />}
        label="Continue with GitHub"
        onClick={handleGithub}
        loading={pending === "github"}
        disabled={pending !== null}
      />
    </div>
  );
}
